// Platform-wide mock data for Super Admin Console
export type PlanTier = "Basic" | "Pro" | "Premium" | "Enterprise";
export type TenantStatus = "Active" | "Suspended" | "Trial" | "Pending";

export interface Tenant {
  id: string;
  name: string;
  code: string;
  industry: string;
  plan: PlanTier;
  activeUsers: number;
  employees: number;
  sites: number;
  status: TenantStatus;
  renewalDate: string;
  createdOn: string;
  primaryContact: string;
  contactEmail: string;
  supportTier: "Standard" | "Priority" | "Dedicated";
  deploymentMode: "Cloud" | "Hybrid" | "On-Prem";
  attendanceToday: number;
  pendingRequests: number;
  region: string;
}

export interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: string;
  tenantId: string;
  tenantName: string;
  status: "Active" | "Disabled" | "Pending";
  lastLogin: string;
  createdDate: string;
}

export interface Plan {
  id: PlanTier;
  name: PlanTier;
  target: string;
  priceFrom: string;
  tenants: number;
  features: string[];
  maxUsers: string;
  highlight?: boolean;
}

export interface AuditLog {
  id: string;
  timestamp: string;
  actor: string;
  role: string;
  tenant: string;
  actionType: string;
  module: string;
  target: string;
  result: "Success" | "Failure" | "Warning";
  ip: string;
  severity: "Info" | "Warning" | "Critical";
}

export interface PlatformNotification {
  id: string;
  title: string;
  body: string;
  scope: "All Tenants" | "Selected Tenants" | "Plan: Pro+" | "Internal";
  severity: "Info" | "Warning" | "Critical" | "Maintenance";
  scheduledAt: string;
  status: "Sent" | "Scheduled" | "Draft";
  delivered: number;
}

export interface PlatformSite {
  id: string;
  siteName: string;
  tenant: string;
  region: string;
  employees: number;
  geofenceRadius: number;
  status: "Active" | "Inactive";
  attendanceToday: number;
  alerts: number;
}

export const tenants: Tenant[] = [
  { id: "T-001", name: "Karya Logistics Sdn Bhd", code: "KARYA", industry: "Logistics", plan: "Enterprise", activeUsers: 1840, employees: 4200, sites: 22, status: "Active", renewalDate: "2026-09-12", createdOn: "2023-04-14", primaryContact: "Hafiz Ramli", contactEmail: "hafiz@karya.my", supportTier: "Dedicated", deploymentMode: "Hybrid", attendanceToday: 3120, pendingRequests: 48, region: "Klang Valley" },
  { id: "T-002", name: "Metro Facilities Group", code: "METRO", industry: "Facility Management", plan: "Premium", activeUsers: 980, employees: 2400, sites: 18, status: "Active", renewalDate: "2026-05-02", createdOn: "2023-08-20", primaryContact: "Cheryl Tan", contactEmail: "cheryl@metro-fg.com", supportTier: "Priority", deploymentMode: "Cloud", attendanceToday: 1820, pendingRequests: 36, region: "Penang" },
  { id: "T-003", name: "Johor Infra Services", code: "JINFRA", industry: "Construction", plan: "Premium", activeUsers: 720, employees: 1900, sites: 14, status: "Active", renewalDate: "2026-06-18", createdOn: "2023-11-04", primaryContact: "Suresh Pillai", contactEmail: "suresh@jinfra.my", supportTier: "Priority", deploymentMode: "Cloud", attendanceToday: 1410, pendingRequests: 22, region: "Johor" },
  { id: "T-004", name: "Nusantara Field Ops", code: "NFO", industry: "Field Services", plan: "Pro", activeUsers: 540, employees: 1320, sites: 11, status: "Active", renewalDate: "2026-04-30", createdOn: "2024-01-22", primaryContact: "Aida Rahman", contactEmail: "aida@nfo.co", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 980, pendingRequests: 28, region: "East Coast" },
  { id: "T-005", name: "KL Operations Holdings", code: "KLOH", industry: "Conglomerate", plan: "Pro", activeUsers: 610, employees: 1480, sites: 9, status: "Active", renewalDate: "2026-07-08", createdOn: "2024-02-11", primaryContact: "Daniel Wong", contactEmail: "daniel@kloh.my", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 1100, pendingRequests: 31, region: "Klang Valley" },
  { id: "T-006", name: "Pacific Retail Network", code: "PRN", industry: "Retail", plan: "Pro", activeUsers: 480, employees: 1100, sites: 8, status: "Active", renewalDate: "2026-04-25", createdOn: "2024-03-08", primaryContact: "Yuki Tanaka", contactEmail: "yuki@pacretail.com", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 820, pendingRequests: 19, region: "Klang Valley" },
  { id: "T-007", name: "Borneo Energy Partners", code: "BEP", industry: "Energy", plan: "Premium", activeUsers: 410, employees: 980, sites: 7, status: "Active", renewalDate: "2026-08-19", createdOn: "2024-04-19", primaryContact: "Rajen Subramaniam", contactEmail: "rajen@borneoenergy.my", supportTier: "Priority", deploymentMode: "Hybrid", attendanceToday: 720, pendingRequests: 24, region: "East Malaysia" },
  { id: "T-008", name: "Sentosa Hospitality Group", code: "SHG", industry: "Hospitality", plan: "Pro", activeUsers: 360, employees: 720, sites: 4, status: "Trial", renewalDate: "2026-04-28", createdOn: "2025-02-02", primaryContact: "Mei Lin", contactEmail: "mei@sentosahg.sg", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 540, pendingRequests: 18, region: "Singapore" },
  { id: "T-009", name: "Selangor City Cleaners", code: "SCC", industry: "Sanitation", plan: "Basic", activeUsers: 180, employees: 380, sites: 2, status: "Active", renewalDate: "2026-05-15", createdOn: "2024-09-05", primaryContact: "Farah Nadia", contactEmail: "farah@scc.my", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 290, pendingRequests: 11, region: "Klang Valley" },
  { id: "T-010", name: "Pulau Tech Park", code: "PTP", industry: "Technology Park", plan: "Basic", activeUsers: 80, employees: 150, sites: 1, status: "Suspended", renewalDate: "2026-03-30", createdOn: "2024-11-12", primaryContact: "Kenneth Lee", contactEmail: "kenneth@ptp.io", supportTier: "Standard", deploymentMode: "Cloud", attendanceToday: 0, pendingRequests: 7, region: "Penang" },
];

export const tenantUsers: TenantUser[] = [
  { id: "U-001", name: "Hafiz Ramli", email: "hafiz@karya.my", role: "Tenant Owner", tenantId: "T-001", tenantName: "Karya Logistics Sdn Bhd", status: "Active", lastLogin: "12 min ago", createdDate: "2023-04-14" },
  { id: "U-002", name: "Nurul Izzah", email: "nurul@karya.my", role: "HR Manager", tenantId: "T-001", tenantName: "Karya Logistics Sdn Bhd", status: "Active", lastLogin: "2 hr ago", createdDate: "2023-05-02" },
  { id: "U-003", name: "Cheryl Tan", email: "cheryl@metro-fg.com", role: "Tenant Owner", tenantId: "T-002", tenantName: "Metro Facilities Group", status: "Active", lastLogin: "1 hr ago", createdDate: "2023-08-20" },
  { id: "U-004", name: "Aiman Rahman", email: "aiman@metro-fg.com", role: "Operations Admin", tenantId: "T-002", tenantName: "Metro Facilities Group", status: "Active", lastLogin: "30 min ago", createdDate: "2023-09-11" },
  { id: "U-005", name: "Suresh Pillai", email: "suresh@jinfra.my", role: "Tenant Owner", tenantId: "T-003", tenantName: "Johor Infra Services", status: "Active", lastLogin: "Yesterday", createdDate: "2023-11-04" },
  { id: "U-006", name: "Daniel Wong", email: "daniel@kloh.my", role: "Tenant Owner", tenantId: "T-005", tenantName: "KL Operations Holdings", status: "Active", lastLogin: "4 hr ago", createdDate: "2024-02-11" },
  { id: "U-007", name: "Aida Rahman", email: "aida@nfo.co", role: "Tenant Owner", tenantId: "T-004", tenantName: "Nusantara Field Ops", status: "Active", lastLogin: "5 hr ago", createdDate: "2024-01-22" },
  { id: "U-008", name: "Farah Nadia", email: "farah@scc.my", role: "Site Admin", tenantId: "T-009", tenantName: "Selangor City Cleaners", status: "Active", lastLogin: "Yesterday", createdDate: "2024-09-05" },
  { id: "U-009", name: "Kenneth Lee", email: "kenneth@ptp.io", role: "Tenant Owner", tenantId: "T-010", tenantName: "Pulau Tech Park", status: "Disabled", lastLogin: "12 days ago", createdDate: "2024-11-12" },
  { id: "U-010", name: "Mei Lin", email: "mei@sentosahg.sg", role: "Tenant Owner", tenantId: "T-008", tenantName: "Sentosa Hospitality Group", status: "Pending", lastLogin: "—", createdDate: "2025-02-02" },
  { id: "U-011", name: "Rajen Subramaniam", email: "rajen@borneoenergy.my", role: "Tenant Owner", tenantId: "T-007", tenantName: "Borneo Energy Partners", status: "Active", lastLogin: "3 hr ago", createdDate: "2024-04-19" },
  { id: "U-012", name: "Yuki Tanaka", email: "yuki@pacretail.com", role: "Tenant Owner", tenantId: "T-006", tenantName: "Pacific Retail Network", status: "Active", lastLogin: "Today", createdDate: "2024-03-08" },
];

export const plans: Plan[] = [
  { id: "Basic", name: "Basic", target: "Small operations · up to 200 users", priceFrom: "RM 499/mo", tenants: 2, maxUsers: "200", features: ["Attendance Monitoring", "Basic Geofence", "1 Site", "Email Support"] },
  { id: "Pro", name: "Pro", target: "Growing teams · up to 1,000 users", priceFrom: "RM 1,499/mo", tenants: 4, maxUsers: "1,000", features: ["Everything in Basic", "Multi-site", "Correction Requests", "Standard Reports", "AI Assistant (Limited)"] },
  { id: "Premium", name: "Premium", target: "Enterprise ops · up to 3,000 users", priceFrom: "RM 3,999/mo", tenants: 3, maxUsers: "3,000", features: ["Everything in Pro", "Advanced Analytics", "Priority Support", "Full AI Assistant", "SSO Integration"], highlight: true },
  { id: "Enterprise", name: "Enterprise", target: "Mission-critical · unlimited", priceFrom: "Custom", tenants: 1, maxUsers: "Unlimited", features: ["Everything in Premium", "Dedicated CSM", "On-prem option", "Custom Integrations", "SLA 99.95%", "Audit & Compliance"] },
];

export const auditLogs: AuditLog[] = [
  { id: "AL-1001", timestamp: "2026-04-20 09:42:11", actor: "Aravind Krishnan", role: "Super Admin", tenant: "Karya Logistics", actionType: "Plan Changed", module: "Subscriptions", target: "T-001 → Enterprise", result: "Success", ip: "203.115.42.18", severity: "Info" },
  { id: "AL-1002", timestamp: "2026-04-20 09:31:55", actor: "Hafiz Ramli", role: "Tenant Owner", tenant: "Karya Logistics", actionType: "User Created", module: "Employees", target: "EMP-2841", result: "Success", ip: "118.200.5.42", severity: "Info" },
  { id: "AL-1003", timestamp: "2026-04-20 09:18:02", actor: "System", role: "Platform", tenant: "Pulau Tech Park", actionType: "Tenant Suspended", module: "Tenants", target: "T-010", result: "Warning", ip: "—", severity: "Warning" },
  { id: "AL-1004", timestamp: "2026-04-20 08:55:14", actor: "Cheryl Tan", role: "Tenant Owner", tenant: "Metro Facilities", actionType: "SSO Configured", module: "Integrations", target: "Azure AD", result: "Success", ip: "175.144.8.12", severity: "Info" },
  { id: "AL-1005", timestamp: "2026-04-20 08:42:30", actor: "Aravind Krishnan", role: "Super Admin", tenant: "—", actionType: "Login", module: "Auth", target: "Platform Console", result: "Success", ip: "203.115.42.18", severity: "Info" },
  { id: "AL-1006", timestamp: "2026-04-20 08:12:48", actor: "Aida Rahman", role: "Tenant Owner", tenant: "Nusantara Field Ops", actionType: "Geofence Updated", module: "Sites", target: "Site A · 250m", result: "Success", ip: "60.51.21.7", severity: "Info" },
  { id: "AL-1007", timestamp: "2026-04-20 07:58:19", actor: "Failed Login", role: "Unknown", tenant: "Borneo Energy", actionType: "Login Failed", module: "Auth", target: "rajen@borneoenergy.my", result: "Failure", ip: "45.83.220.11", severity: "Critical" },
  { id: "AL-1008", timestamp: "2026-04-20 07:30:00", actor: "System", role: "Platform", tenant: "All", actionType: "Daily Backup", module: "System", target: "DB Snapshot", result: "Success", ip: "—", severity: "Info" },
  { id: "AL-1009", timestamp: "2026-04-19 22:15:42", actor: "Aravind Krishnan", role: "Super Admin", tenant: "Sentosa Hospitality", actionType: "Trial Extended", module: "Subscriptions", target: "T-008 +14 days", result: "Success", ip: "203.115.42.18", severity: "Info" },
  { id: "AL-1010", timestamp: "2026-04-19 18:02:11", actor: "Daniel Wong", role: "Tenant Owner", tenant: "KL Operations", actionType: "Bulk Approval", module: "Requests", target: "24 records", result: "Success", ip: "60.51.99.4", severity: "Info" },
];


export const platformNotifications: PlatformNotification[] = [
  { id: "PN-01", title: "Scheduled Maintenance · April 28", body: "Platform will undergo maintenance from 02:00-04:00 MYT. Expect brief downtime.", scope: "All Tenants", severity: "Maintenance", scheduledAt: "2026-04-28 02:00", status: "Scheduled", delivered: 0 },
  { id: "PN-02", title: "AI Assistant v2.4 Released", body: "Faster responses, improved attendance summarization, and new admin prompt library.", scope: "Plan: Pro+", severity: "Info", scheduledAt: "2026-04-19 10:00", status: "Sent", delivered: 8 },
  { id: "PN-03", title: "Unusual Login Pattern · Borneo Energy", body: "Multiple failed login attempts detected. Tenant admin notified.", scope: "Selected Tenants", severity: "Critical", scheduledAt: "2026-04-20 07:58", status: "Sent", delivered: 1 },
  { id: "PN-04", title: "Q2 Compliance Reminder", body: "Please review your data retention settings before May 31.", scope: "All Tenants", severity: "Warning", scheduledAt: "2026-04-15 09:00", status: "Sent", delivered: 10 },
  { id: "PN-05", title: "New Geofence Visualization (Beta)", body: "Map-based geofence editor now available for Premium and Enterprise tenants.", scope: "Plan: Pro+", severity: "Info", scheduledAt: "2026-04-22 09:00", status: "Draft", delivered: 0 },
];

export const platformSites: PlatformSite[] = [
  { id: "PS-01", siteName: "Karya · KL HQ", tenant: "Karya Logistics", region: "Klang Valley", employees: 480, geofenceRadius: 200, status: "Active", attendanceToday: 412, alerts: 3 },
  { id: "PS-02", siteName: "Karya · Port Klang Yard", tenant: "Karya Logistics", region: "Klang Valley", employees: 320, geofenceRadius: 350, status: "Active", attendanceToday: 290, alerts: 5 },
  { id: "PS-03", siteName: "Metro · Penang Tower", tenant: "Metro Facilities", region: "Penang", employees: 220, geofenceRadius: 150, status: "Active", attendanceToday: 198, alerts: 2 },
  { id: "PS-04", siteName: "Johor Infra · Site A", tenant: "Johor Infra Services", region: "Johor", employees: 180, geofenceRadius: 250, status: "Active", attendanceToday: 162, alerts: 1 },
  { id: "PS-05", siteName: "NFO · East Coast Hub", tenant: "Nusantara Field Ops", region: "East Coast", employees: 240, geofenceRadius: 300, status: "Active", attendanceToday: 210, alerts: 4 },
  { id: "PS-06", siteName: "KLOH · KLCC Office", tenant: "KL Operations Holdings", region: "Klang Valley", employees: 160, geofenceRadius: 120, status: "Active", attendanceToday: 144, alerts: 0 },
  { id: "PS-07", siteName: "Pacific · Mid Valley Mall", tenant: "Pacific Retail Network", region: "Klang Valley", employees: 95, geofenceRadius: 100, status: "Active", attendanceToday: 88, alerts: 1 },
  { id: "PS-08", siteName: "Borneo · Miri Refinery", tenant: "Borneo Energy Partners", region: "East Malaysia", employees: 320, geofenceRadius: 500, status: "Active", attendanceToday: 295, alerts: 6 },
  { id: "PS-09", siteName: "PTP · Tech Hub", tenant: "Pulau Tech Park", region: "Penang", employees: 60, geofenceRadius: 80, status: "Inactive", attendanceToday: 0, alerts: 0 },
];

export const platformKpis = {
  totalTenants: 10,
  activeUsers: 6200,
  totalEmployees: 18400,
  attendanceToday: 11850,
  pendingRequests: 284,
  activeSites: 96,
  renewalsDue: 3,
  aiInteractionsToday: 1420,
  mobileActiveUsers: 4820,
  platformAlerts: 7,
};

// Tenant growth — last 6 months
export const tenantGrowth = [
  { month: "Nov", tenants: 6 },
  { month: "Dec", tenants: 7 },
  { month: "Jan", tenants: 8 },
  { month: "Feb", tenants: 8 },
  { month: "Mar", tenants: 9 },
  { month: "Apr", tenants: 10 },
];

export const attendanceVolume = [
  { day: "Mon", records: 10240 },
  { day: "Tue", records: 11120 },
  { day: "Wed", records: 11540 },
  { day: "Thu", records: 11320 },
  { day: "Fri", records: 11850 },
  { day: "Sat", records: 6420 },
  { day: "Sun", records: 3210 },
];

export const planDistribution = [
  { plan: "Basic", value: 2, fill: "hsl(var(--muted-foreground))" },
  { plan: "Pro", value: 4, fill: "hsl(var(--info))" },
  { plan: "Premium", value: 3, fill: "hsl(var(--primary))" },
  { plan: "Enterprise", value: 1, fill: "hsl(var(--accent))" },
];

export const requestsByStatus = [
  { status: "Pending", count: 284 },
  { status: "Approved", count: 1820 },
  { status: "Rejected", count: 142 },
  { status: "Clarification", count: 64 },
];

export const topTenants = [...tenants]
  .sort((a, b) => b.attendanceToday - a.attendanceToday)
  .slice(0, 5);

export const aiUsageByPlan = [
  { plan: "Basic", interactions: 80 },
  { plan: "Pro", interactions: 420 },
  { plan: "Premium", interactions: 620 },
  { plan: "Enterprise", interactions: 300 },
];
